/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 18/05/22
 */

import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { Tile } from '@/components/Tile';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import StateDisplay from '@/components/StateDisplay/StateDisplay';
import WorkloadEditorForm from '@/components/pages/Admin/Workload/WorkloadEditorForm';
import { Workplan, WorkplanState } from '@/wpt-lib/interfaces/Workplan';

export interface UpdateAssignmentStateDTO {
    inactive: boolean;
    reason: string;
}

const WorkloadEditor = ({
    assignment,
    currentAssignment,
}: {
    assignment: Assignment;
    currentAssignment: Assignment;
}) => {
    const {
        error: getError,
        makeRequest: makeGetRequest,
        responseData: loadedAssignment,
        loading,
    } = useGetRequest<Assignment>();

    const { makeRequest: getWorkplans, responseData: workplans } = useGetRequest<Workplan[]>();

    useAsyncEffect(async () => {
        await Promise.all([
            makeGetRequest(`assignment/${assignment.id}`),
            getWorkplans(`/workplan/current/${assignment.id}`, {
                params: { assignment: currentAssignment.id },
            }),
        ]);
    }, [assignment]);

    const currentYear = new Date().getFullYear();
    const updatedableWorkplans = workplans
        ?.filter((w) => w.year > currentYear && ![WorkplanState.ENDORSED, WorkplanState.HISTORICAL].includes(w.state))
        .map((w) => w.year)
        .sort();

    return (
        <Tile colspan={2} collapsable overflowX="auto" heading={'Workload Allocation'} defaultOpen>
            <VStack alignItems={'flex-start'} spacing={3}>
                <Text>
                    This section lets you adjust the workload allocation for this individual. Please note, changes in
                    this section will apply for the current ({currentYear}) and future years{' '}
                    {updatedableWorkplans?.length ? `(${updatedableWorkplans.join(', ')})` : ''} workplans.
                </Text>

                <StateDisplay
                    loading={loading}
                    error={!!getError}
                    data={loadedAssignment}
                    errorRender={() => <SimpleAlert variant="subtle">Could not workload balance</SimpleAlert>}
                    render={(data) => (
                        <>
                            <HStack>
                                <Text fontWeight="bold">Category</Text>
                                <Text>
                                    {data.workloadCategory}
                                    {data.hasCustomWorkloadPortions && ' (Modified)'}
                                </Text>
                            </HStack>
                            <WorkloadEditorForm
                                workload={assignment}
                                currentAssignment={currentAssignment}
                                loading={loading}
                                saveUrl={`assignment/${assignment.id}`}
                            />
                        </>
                    )}
                />
            </VStack>
        </Tile>
    );
};

export default WorkloadEditor;
